import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useHealthProviderFeatureValues } from '../../../data/data/features/use-health-provider-feature-values';
import { HealthProviderAvailable } from '../../../data/types/api/graphql';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import {
  OpeningHoursFormattedItem,
  OpeningHoursGroupedData,
} from './opening-hours-list.types';
import { OpeningHoursListItem } from './opening-hours-list-item';

type OpeningHoursListProps = {
  loading?: boolean;
  items: HealthProviderAvailable[];
};

export function OpeningHoursList({
  loading = false,
  items,
}: OpeningHoursListProps) {
  const { data: healthProviderFeatures } = useHealthProviderFeatureValues();
  const intl = useIntl();
  const groupedData = useMemo<OpeningHoursGroupedData>(() => {
    if (!items) {
      return [];
    }

    // First format all the dates and times
    const formattedItems: OpeningHoursFormattedItem[] = items.map(item => ({
      ...item,

      features: healthProviderFeatures.map(f => ({
        title: f.title,
        key: f.key,
        value: item.appFeatures.includes(f.key) ?? false,
      })),

      startDate: intl.formatDate(item.startTime, {
        weekday: 'long', // Add weekday (Monday, Tuesday, etc.)
        day: 'numeric', // Just the number without leading zero
        month: 'long', // Full month name (April)
        year: 'numeric', // Full year (2025)
      }),
      endDate: intl.formatDate(item.endTime, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
      startTime: intl.formatDate(item.startTime, {
        hour: 'numeric',
        minute: 'numeric',
      }),
      endTime: intl.formatDate(item.endTime, {
        hour: 'numeric',
        minute: 'numeric',
      }),
    }));

    // Group by startDate into a temporary record
    const groupedByDate = formattedItems.reduce((acc, item) => {
      const { startDate } = item;
      if (!acc[startDate]) {
        acc[startDate] = [];
      }
      acc[startDate].push(item);
      return acc;
    }, {} as Record<string, OpeningHoursFormattedItem[]>);

    // Convert the record to an array of grouped items
    return Object.entries(groupedByDate).map(([date, dateItems]) => ({
      date,
      items: dateItems,
    }));
  }, [items, healthProviderFeatures, intl]);

  if (loading) {
    return (
      <p>
        <CustomFormattedMessage id="common.loading" />
      </p>
    );
  }

  if (isEmpty(items)) {
    return (
      <p>
        <CustomFormattedMessage id="common.no-data" />
      </p>
    );
  }

  return (
    <div className="content-list">
      {groupedData.map(group => (
        <div key={group.date} className="content-list__item">
          <OpeningHoursListItem item={group} />
        </div>
      ))}
    </div>
  );
}
